import React from 'react';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const UNASSIGNED = '__none__';

// One editable AI task draft: title, description, priority, hours, due date, assignee, team, subtasks.
export const TaskDraftCard = ({ draft, index, teams, members, creating, onChange, onRemove, onCreate }) => {
  const id = draft._id;
  const set = (patch) => onChange(id, patch);
  const setSubtask = (i, value) => set({ subtasks: draft.subtasks.map((s, j) => (j === i ? value : s)) });

  return (
    <div className={`draft-card priority-${draft.priority || 'medium'}`} data-testid={`task-draft-${index}`}>
      <div className="draft-head">
        <span className="draft-index">Task {index + 1}</span>
        <button type="button" className="draft-remove" onClick={() => onRemove(id)} aria-label="Discard draft" data-testid={`draft-remove-${index}`}>
          <X size={16} />
        </button>
      </div>

      <label className="draft-field">
        <span>Title</span>
        <Input value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder="Task title" data-testid={`draft-title-${index}`} />
      </label>
      <label className="draft-field">
        <span>Description</span>
        <Textarea rows={3} value={draft.description} onChange={(e) => set({ description: e.target.value })} placeholder="What needs to be done" data-testid={`draft-description-${index}`} />
      </label>

      <div className="draft-grid">
        <label className="draft-field">
          <span>Priority</span>
          <Select value={draft.priority || 'medium'} onValueChange={(v) => set({ priority: v })}>
            <SelectTrigger data-testid={`draft-priority-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="draft-field">
          <span>Estimated hours</span>
          <Input type="number" min="0" step="0.5" value={draft.estimated_hours} onChange={(e) => set({ estimated_hours: e.target.value })} placeholder="—" data-testid={`draft-hours-${index}`} />
        </label>
        <label className="draft-field">
          <span>Due date</span>
          <Input type="date" value={draft.due_date} onChange={(e) => set({ due_date: e.target.value })} data-testid={`draft-due-${index}`} />
        </label>
        <label className="draft-field">
          <span>Assignee</span>
          <Select value={draft.assigned_to || UNASSIGNED} onValueChange={(v) => set({ assigned_to: v === UNASSIGNED ? '' : v })}>
            <SelectTrigger data-testid={`draft-assignee-${index}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
              {(members || []).map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}{m.is_pending ? ' (invited)' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="draft-field">
          <span>Team / board</span>
          <Select value={draft.team_id} onValueChange={(v) => set({ team_id: v, assigned_to: '' })}>
            <SelectTrigger data-testid={`draft-team-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
      </div>

      <div className="draft-field">
        <span>Subtasks</span>
        <div className="draft-subtasks">
          {draft.subtasks.map((s, i) => (
            <div className="draft-subtask" key={`${id}-sub-${i}`}>
              <Input value={s} onChange={(e) => setSubtask(i, e.target.value)} placeholder={`Step ${i + 1}`} data-testid={`draft-subtask-${index}-${i}`} />
              <button type="button" className="draft-remove" onClick={() => set({ subtasks: draft.subtasks.filter((_, j) => j !== i) })} aria-label="Remove subtask" data-testid={`draft-subtask-remove-${index}-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button type="button" className="draft-add" onClick={() => set({ subtasks: [...draft.subtasks, ''] })} data-testid={`draft-subtask-add-${index}`}>
            <Plus size={14} /> Add subtask
          </button>
        </div>
      </div>

      <div className="draft-actions">
        <Button type="button" onClick={() => onCreate(draft)} disabled={creating || !draft.title.trim()} data-testid={`draft-create-${index}`}>
          {creating ? <Loader2 size={16} className="animate-spin" /> : 'Create task'}
        </Button>
      </div>
    </div>
  );
};
